import { useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SEOJsonLd from '../../components/feature/SEOJsonLd';

const plans = [
  {
    name: 'Growth',
    price: '$49',
    period: '/mo',
    tagline: 'For veterinarians getting started',
    highlight: false,
    badge: null,
    features: [
      { text: '0% commission on veterinary services', strong: true },
      { text: 'Digital services and consults' },
      { text: 'Curated product sales' },
      { text: 'Standard product margins' },
      { text: 'Standard processing fees' },
      { text: 'Vetuvia subdomain' },
      { text: 'Basic analytics' },
      { text: 'Email support' },
    ],
  },
  {
    name: 'Elite',
    price: '$99',
    period: '/mo',
    tagline: 'For veterinarians ready to grow faster',
    highlight: true,
    badge: 'Most Popular',
    features: [
      { text: 'Everything in Growth', strong: true },
      { text: 'Higher product margins' },
      { text: 'Lower processing fees' },
      { text: 'Custom domain' },
      { text: 'Advanced analytics' },
      { text: 'Priority support' },
      { text: 'Premium branding tools' },
    ],
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    tagline: 'For clinics, groups, and larger organizations',
    highlight: false,
    badge: null,
    features: [
      { text: 'Everything in Elite', strong: true },
      { text: 'Multi-provider management' },
      { text: 'Admin controls and permissions' },
      { text: 'Custom onboarding' },
      { text: 'Dedicated support' },
      { text: 'Custom integrations' },
      { text: 'Enterprise reporting' },
    ],
  },
];

const faqs = [
  {
    q: 'Does Vetuvia take a cut of my service revenue?',
    a: 'No. Vetuvia takes 0% commission on veterinary services — teleconsults, memberships, and any service you deliver directly. Revenue sharing applies only to optional curated products and platform-enabled programs.',
  },
  {
    q: 'Can I upgrade or downgrade my plan?',
    a: 'Yes. You can change your plan at any time. Changes take effect at the start of your next billing cycle.',
  },
  {
    q: 'What counts as a "veterinary service"?',
    a: 'Any service you deliver as a licensed veterinarian — teleconsults, advice sessions, memberships, and care plans. Product sales and platform-enabled programs have separate margin structures.',
  },
  {
    q: 'Is there a free trial?',
    a: 'Vetuvia is currently invite-only. Request an invitation and our team will be in touch with onboarding details.',
  },
];

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://vetuvia.com';

export default function PricingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const pricingTiers = plans.map((p) => ({
    name: p.name,
    price: p.price,
    description: p.tagline,
  }));

  const handleCTAClick = () => {
    window.dispatchEvent(new Event('openInvitationForm'));
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEOJsonLd
        title="Pricing Plans – Vetuvia | Flexible Plans for Veterinarian Income Growth"
        description="Choose the perfect Vetuvia plan for your veterinary practice. Transparent pricing for teleconsults, memberships, and subscription care plans. 0% commission on services."
        keywords="Vetuvia pricing, veterinary platform plans, vet telehealth pricing, veterinarian membership plans, subscription care pricing"
        canonical={`${siteUrl}/pricing`}
        ogImage="https://static.readdy.ai/image/f30444846e2254d95b405efc135ce378/b4d09c4f03ec4a157d9695deb6f4dc19.png"
        schemaType="PricingPage"
        pricingTiers={pricingTiers}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-40 pb-16 px-6 lg:px-12 text-center bg-gradient-to-br from-[#F8FFFE] via-white to-[#F0F9F7]">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#4DB8D4]/10 rounded-full mb-6">
            <i className="ri-price-tag-3-line text-[#4DB8D4]"></i>
            <span className="text-sm font-medium text-[#0A1628]">Transparent Pricing</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-[#0A1628] leading-tight mb-5">
            Keep <span className="text-[#4DB8D4]">100%</span> of your<br />service revenue
          </h1>
          <p className="text-base text-gray-500 leading-relaxed max-w-xl mx-auto">
            Vetuvia takes <strong>0% commission</strong> on veterinary services. Revenue sharing applies only to optional products, subscriptions, and platform-enabled programs.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6 items-stretch">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative flex flex-col rounded-2xl border transition-all duration-300 hover:scale-[1.02] ${
                plan.highlight
                  ? 'bg-gradient-to-br from-[#4DB8D4] to-[#3A9BB0] border-[#4DB8D4] shadow-2xl shadow-[#4DB8D4]/30 scale-[1.02]'
                  : 'bg-white border-gray-200 shadow-sm hover:border-[#4DB8D4]/50 hover:shadow-xl'
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-semibold px-4 py-1.5 rounded-full whitespace-nowrap shadow-lg">
                    {plan.badge}
                  </span>
                </div>
              )}

              <div className="p-8 flex flex-col flex-1">
                {/* Plan name */}
                <p className={`text-xs font-semibold tracking-widest uppercase mb-3 ${plan.highlight ? 'text-white/90' : 'text-[#4DB8D4]'}`}>
                  {plan.name}
                </p>

                {/* Price */}
                <div className="flex items-end gap-1 mb-2">
                  <span className={`text-4xl font-bold leading-none ${plan.highlight ? 'text-white' : 'text-[#0A1628]'}`}>
                    {plan.price}
                  </span>
                  {plan.period && (
                    <span className={`text-sm mb-1 ${plan.highlight ? 'text-white/80' : 'text-gray-400'}`}>
                      {plan.period}
                    </span>
                  )}
                </div>

                {/* Tagline */}
                <p className={`text-sm mb-7 ${plan.highlight ? 'text-white/90' : 'text-gray-500'}`}>
                  {plan.tagline}
                </p>

                {/* Divider */}
                <div className={`border-t mb-6 ${plan.highlight ? 'border-white/20' : 'border-gray-100'}`} />

                {/* Features */}
                <ul className="space-y-3 flex-1 mb-8">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center mt-0.5 shrink-0 ${plan.highlight ? 'bg-white/20' : 'bg-[#4DB8D4]/10'}`}>
                        <i className={`ri-check-line text-sm font-bold ${plan.highlight ? 'text-white' : 'text-[#4DB8D4]'}`}></i>
                      </div>
                      <span className={`text-sm leading-snug ${f.strong ? 'font-semibold' : 'font-normal'} ${plan.highlight ? (f.strong ? 'text-white' : 'text-white/90') : (f.strong ? 'text-[#0A1628]' : 'text-gray-600')}`}>
                        {f.text}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <button
                  onClick={handleCTAClick}
                  className={`w-full py-3.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap cursor-pointer shadow-md hover:shadow-lg ${
                    plan.highlight
                      ? 'bg-white text-[#4DB8D4] hover:bg-gray-50'
                      : plan.name === 'Enterprise'
                      ? 'bg-[#0A1628] hover:bg-[#162040] text-white'
                      : 'bg-[#0A1628] hover:bg-[#1a2f52] text-white'
                  }`}
                >
                  {plan.name === 'Enterprise' ? 'Contact Us' : 'Request Invitation'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Zero Commission Callout */}
        <div className="max-w-5xl mx-auto mt-10 bg-gradient-to-r from-[#F0F9F7] to-[#F8FFFE] border border-[#4DB8D4]/20 rounded-2xl px-8 py-6 flex flex-col md:flex-row items-center gap-4 text-center md:text-left">
          <div className="w-12 h-12 flex items-center justify-center bg-[#4DB8D4]/10 rounded-xl flex-shrink-0">
            <i className="ri-shield-check-line text-2xl text-[#4DB8D4]"></i>
          </div>
          <div>
            <p className="font-semibold text-[#0A1628] text-base">0% Commission on Veterinary Services</p>
            <p className="text-sm text-gray-500 mt-0.5">You keep every dollar earned from teleconsults and direct services. Revenue sharing only applies to optional platform-enabled programs and products.</p>
          </div>
        </div>
      </section>

      {/* Zero Commission Banner */}
      <section className="py-14 px-6 lg:px-12 bg-gradient-to-br from-[#F8FFFE] via-white to-[#F0F9F7]">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-16">
          <div className="flex-1 text-center md:text-left">
            <p className="text-xs font-semibold tracking-widest uppercase text-[#4DB8D4] mb-3">Our Commitment</p>
            <h2 className="text-2xl font-bold text-[#0A1628] mb-3 leading-snug">
              Your expertise earns what it deserves
            </h2>
            <p className="text-sm text-gray-500 leading-relaxed">
              We built Vetuvia on a simple principle: veterinarians should keep the revenue their skills generate. Platform fees cover infrastructure, tools, and support — never your service income.
            </p>
          </div>
          <div className="flex-shrink-0 grid grid-cols-2 gap-4">
            {[
              { value: '0%', label: 'Commission on services' },
              { value: '100%', label: 'Service revenue yours' },
              { value: '$0', label: 'Hidden fees' },
              { value: '∞', label: 'Earning potential' },
            ].map((stat) => (
              <div key={stat.label} className="bg-white rounded-xl border border-[#4DB8D4]/20 shadow-sm px-6 py-5 text-center hover:shadow-md transition-shadow">
                <p className="text-2xl font-bold text-[#4DB8D4] mb-1">{stat.value}</p>
                <p className="text-xs text-gray-500 leading-tight">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-2xl mx-auto">
          <p className="text-xs font-semibold tracking-widest uppercase text-[#4DB8D4] text-center mb-3">FAQ</p>
          <h2 className="text-2xl font-bold text-[#0A1628] text-center mb-10">Common questions about pricing</h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="border border-gray-200 rounded-xl overflow-hidden hover:border-[#4DB8D4]/50 hover:shadow-lg transition-all duration-300"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer hover:bg-[#F8FFFE] transition-colors"
                >
                  <span className="text-sm font-semibold text-[#0A1628] pr-4">{faq.q}</span>
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-[#4DB8D4] text-base`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 bg-[#F8FFFE]">
                    <p className="text-sm text-gray-500 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-[#0A1628] to-[#1a2f52] rounded-3xl p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#4DB8D4]/10 to-transparent"></div>
            <div className="relative z-10">
              <p className="text-xs font-semibold tracking-widest uppercase text-[#4DB8D4] mb-4">Get Started</p>
              <h2 className="text-3xl font-bold text-white mb-4 leading-snug">
                Earn More — Without More Clinic Hours
              </h2>
              <p className="text-sm text-gray-400 mb-8 leading-relaxed max-w-xl mx-auto">
                Join veterinarians building flexible, modern practices on Vetuvia.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={handleCTAClick}
                  className="px-8 py-3.5 bg-[#4DB8D4] hover:bg-[#3aa8c4] text-white rounded-xl font-semibold text-sm transition-all whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl hover:scale-105 inline-flex items-center gap-2"
                >
                  Request Invitation
                  <i className="ri-arrow-right-line text-lg"></i>
                </button>
                <button className="px-8 py-3.5 bg-white/10 text-white text-sm font-semibold rounded-xl hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer border border-white/30 inline-flex items-center gap-2">
                  <i className="ri-calendar-line text-lg"></i>
                  Schedule a Demo
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
